import { SkeletonRows } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FileText,
  Search,
  Plus,
  Trash2,
  X,
  ShieldCheck,
  UploadCloud,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Clock,
  History,
  Cpu,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './firmwares.css';

const BASE_URL = 'https://robopower.xyz';

// Keep repository entries in newest-first order regardless of the order returned by an API.
const getFirmwareTimestamp = (firmware) => {
  const timestamp = new Date(firmware?.timestamp).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const Firmwares = () => {
  // 1. Categories Mapping
  const categories = [
    { label: 'Control Board', key: 'cbfirmwares', get: '/v2/cbfw/cbgetallfirmwares', post: '/v2/cbfw/cbfwUpload', delete: '/v2/cbfw/deletefw?id=' },
    { label: 'Control Board V2', key: 'cbv2firmwares', get: '/v2/cbfwv2/cbgetallfirmwares', post: '/v2/cbfwv2/cbfwUpload', delete: '/v2/cbfwv2/deletefw?id=' },
    { label: 'Sampler (OLED)', key: 'firmwares', get: '/v2/fw/oledgetallfirmwares', post: '/v2/fw/oledfwUpload', delete: '/v2/fw/deleteoledfw?id=' },
    { label: 'TFT', key: 'tftfirmwares', get: '/v2/fw/tftgetallfirmwares', post: '/v2/fw/tftfwUpload', delete: '/v2/fw/deletetftfw?id=' },
    { label: 'Pressure OLED', key: 'pressfirmwares', get: '/v2/fw/oled_mpxv_pressgetallfirmwares', post: '/v2/fw/oled_mpxv_pressfwUpload', delete: '/v2/fw/deletempxvfw?id=' },
    { label: 'CA_TFT', key: 'catftfirmwares', get: '/v2/catftfw/catftgetallfirmwares', post: '/v2/catftfw/catftfwUpload', delete: '/v2/catftfw/deletefw?id=' },
    { label: 'WI_TFT', key: 'witftfirmwares', get: '/v2/witftfw/witftgetallfirmwares', post: '/v2/witftfw/witftfwUpload', delete: '/v2/witftfw/deletefw?id=' },
    { label: 'IL_TFT', key: 'iltftfirmwares', get: '/v2/iltftfw/iltftgetallfirmwares', post: '/v2/iltftfw/iltftfwUpload', delete: '/v2/iltftfw/deletefw?id=' },
    { label: 'S3_SAMPLER', key: 's3firmwares', get: '/v2/s3fw/getallfirmwaress3', post: '/v2/s3fw/fwUploads3', delete: '/v2/s3fw/deletes3fw?id=' },
    { label: 'CA-ZIGBEE', key: 'cazigfirmwares', get: '/v2/cazigfw/caziggetallfirmwares', post: '/v2/cazigfw/cazigfwUpload', delete: '/v2/cazigfw/deletefw?id=' },
    { label: 'INDIA-CONTROLBOARD', key: 'cbindiafirmwares', get: '/v1/cbota/cbgetallfirmwares', post: '/v1/cbota/cbfwUpload', delete: '/v1/cbota/deletefw?id=' },
    { label: 'INDIA-SAMPLER', key: 'sampindiafirmwares', get: '/v1/sampota/getallfirmwares', post: '/v1/sampota/fwUpload', delete: '/v1/sampota/deletefw?id=' },
    { label: 'Device manager', key: 'guifirmwares', get: '/v2/guifw/getallfirmwares', post: '/v2/guifw/fwUpload', delete: '/guifw/deleteguifw?id=' },
    { label: 'Neoway N-58', key: 'neowayfirmwares', get: '/v3/neowayfw/getallfirmwaresneo', post: '/v3/neowayfw/fwUploadneo', delete: '/v3/neowayfw/deletefw?id=' },
    { label: 'A300-IND-SAM', key: 'a300insamfirmwares', get: '/v3/firmwares/indsampgetallfirmwares', post: '/v3/firmwares/indsampfwUpload', delete: '/v3/firmwares/inddeletesampfw?id=' },
    { label: 'A300-US-SAM', key: 'a300ussamfirmwares', get: '/v3/firmwares/ussampgetallfirmwares', post: '/v3/firmwares/ussampfwUpload', delete: '/v3/firmwares/usdeletesampfw?id=' },
    { label: 'A300-US-CB', key: 'a300uscbfirmwares', get: '/v3/firmwares/uscbgetallfirmwares', post: '/v3/firmwares/uscbfwUpload', delete: '/v3/firmwares/usdeletefw?id=' },
    { label: 'A200-US-NEW', key: 'a200ussamfirmwares', get: '/v2/canewtftfw/canewtftgetallfirmwares', post: '/v2/canewtftfw/canewtftfwUpload', delete: '/v2/canewtftfw/deletenewfw?id=' }
    // { label: 'A200-IND-NEW', key: 'a200indiasampfirmwares', get: '/v2/indianewtftfw/indianewtftgetallfirmwares', post: '/v2/indianewtftfw/indianewtft', delete: '/v2/indianewtftfw/deleteindianewfw?id=' },

  ];

  // 2. State
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [firmwareData, setFirmwareData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const tabsRef = useRef(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Add Form
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [comment, setComment] = useState('');
  const [uploadValidationError, setUploadValidationError] = useState('');

  // Delete Form (Secure)
  const [deleteItem, setDeleteItem] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

    const resetAddFirmwareForm =() => {
  setFile(null);
  setVersion('');
  setComment('');
  setUploadValidationError('');
  setUploadProgress(0);
};

const closeAddModal = () => {
  if (isUploading) return;

  resetAddFirmwareForm();
  setIsAddModalOpen(false);
 };

  // 3. Effects
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        categories.map(cat => axios.get(`${BASE_URL}${cat.get}`).catch(() => ({ data: [] })))
      );

      const newData = {};
      results.forEach((res, index) => {
        newData[categories[index].key] = res.data || [];
      });
      setFirmwareData(newData);
    } catch (error) {
      console.error('Error fetching firmwares:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async (index) => {
    const cat = categories[index];
    try {
      const res = await axios.get(`${BASE_URL}${cat.get}`);
      setFirmwareData(prev => ({ ...prev, [cat.key]: res.data || [] }));
    } catch (error) {
      console.error(`Error fetching ${cat.label}:`, error);
    }
  };

  const scrollTabs = (direction) => {
    let newIndex = activeTabIndex;
    if (direction === 'left' && activeTabIndex > 0) {
      newIndex = activeTabIndex - 1;
    } else if (direction === 'right' && activeTabIndex < categories.length - 1) {
      newIndex = activeTabIndex + 1;
    }
    
    if (newIndex !== activeTabIndex) {
      setActiveTabIndex(newIndex);
      setCurrentPage(1);
      
      // Also scroll the tab into view
      if (tabsRef.current) {
        const tabElements = tabsRef.current.querySelectorAll('.tab-item');
        if (tabElements[newIndex]) {
          tabElements[newIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  };

  // Auto-scroll active tab into view
  useEffect(() => {
    if (tabsRef.current) {
      const activeTab = tabsRef.current.querySelector('.tab-item.active');
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeTabIndex]);

  // 4. Logic
  const activeCategory = categories[activeTabIndex];
  const currentList = useMemo(
    () => [...(firmwareData[activeCategory.key] || [])]
      .sort((a, b) => getFirmwareTimestamp(b) - getFirmwareTimestamp(a)),
    [firmwareData, activeCategory.key]
  );

  const filteredItems = useMemo(() => {
    if (!searchTerm) return currentList;
    const term = searchTerm.toLowerCase();
    return currentList.filter(item =>
      [item.fileName, item.version, item.comment]
        .some(field => field?.toString().toLowerCase().includes(term))
    );
  }, [searchTerm, currentList]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
  const indexOfFirst = (currentPage - 1) * rowsPerPage;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  // 5. Actions
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !version || !comment) {
      setUploadValidationError('Select a firmware file and complete the version and release-notes fields.');
      return;
    }

    setUploadValidationError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('version', version);
    formData.append('comment', comment);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await axios.post(`${BASE_URL}${activeCategory.post}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(percent);
        }
      });

       resetAddFirmwareForm();
  setIsAddModalOpen(false);
      fetchCategoryData(activeTabIndex);
      Swal.fire('Success', 'Firmware uploaded successfully!', 'success');
    } catch (error) {
      console.error('Upload error:', error);
      setUploadValidationError(
    error?.response?.data?.message ||
    'Failed to upload firmware. Please try again.'
  );
    } finally {
      setIsUploading(false);
    }
  };
  const handleDeleteInitiate = (item) => {
    setDeleteItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleSecureDelete = async (e) => {
    e.preventDefault();
    if (!adminEmail || !adminPassword) return;

    try {
      // Verify Admin Credentials
      const verifyUrl = `${BASE_URL}/us/calogin/getuniquelogin?emailId=${adminEmail}&password=${adminPassword}`;
      const verifyRes = await axios.get(verifyUrl);

      if (verifyRes.data && verifyRes.data.length > 0) {
        const logindata = verifyRes.data[0];
        if (adminEmail === logindata.emailId && adminPassword === logindata.password) {
          // Proceed with deletion
          const deleteUrl = `${BASE_URL}${activeCategory.delete}${deleteItem.id}&version=${deleteItem.version}`;
          await axios.get(deleteUrl); // API uses GET for delete as per Vue ref

          setIsDeleteModalOpen(false);
          setAdminEmail('');
          setAdminPassword('');
          fetchCategoryData(activeTabIndex);
          Swal.fire('Deleted', 'Firmware has been removed', 'success');
        } else {
          Swal.fire('Error', 'Invalid credentials', 'error');
        }
      } else {
        Swal.fire('Error', 'Invalid credentials', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      Swal.fire('Error', 'An error occurred during deletion', 'error');
    }
  };

  

  // 6. UI Render
  return (
    <div className="firmwares-container">
      <div className="firmwares-card">
        {/* Header */}
        <div className="firmwares-header">
          <div className="header-left">
            <div className="header-icon">
              <Cpu size={24} />
            </div>
            <div>
              <h1 className="header-title">Firmware Repository</h1>
              <p className="header-subtitle">Manage system updates and device control versions</p>
            </div>
          </div>
          <button className="add-fw-btn"
           onClick={() => {
                resetAddFirmwareForm();
            setIsAddModalOpen(true)}}
           >
            <Plus size={18} />
            <span>Add Firmware</span>
          </button>
        </div>

        {/* Categories Tabs */}
      <div className="firmwares-tabs-container">
        <button 
          className="tabs-arrow left" 
          onClick={() => scrollTabs('left')}
          disabled={activeTabIndex === 0}
        >
          <ChevronLeft size={20} />
        </button>
        <div className="firmwares-tabs-scroll" ref={tabsRef}>
          <div className="firmwares-tabs">
            {categories.map((cat, idx) => (
              <button
                key={idx}
                className={`tab-item ${activeTabIndex === idx ? 'active' : ''}`}
                onClick={() => { setActiveTabIndex(idx); setCurrentPage(1); }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
        <button 
          className="tabs-arrow right" 
          onClick={() => scrollTabs('right')}
          disabled={activeTabIndex === categories.length - 1}
        >
          <ChevronRight size={20} />
        </button>
      </div>

        {/* Search & Stats */}
        <div className="firmwares-toolbar">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              // placeholder="Search by version, file, or comment..."
              placeholder={
    window.innerWidth <= 468
      ? "Search ..."
      : "Search by version, file, or comment..."
  }
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
            {searchTerm && <X size={16} className="clear-search" onClick={() => { setSearchTerm(''); setCurrentPage(1); }} />}
          </div>
          <div className="stats-box">
            <span className="stats-label">Total Versions:</span>
            <span className="stats-value">{filteredItems.length}</span>
          </div>
        </div>

        {/* Table Content */}
        <div className="firmwares-table-wrapper">
          <table className="firmwares-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Release Date</th>
                <th>Version</th>
                <th>Comment</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <SkeletonRows columns={5} /> : paginatedItems.length > 0 ? (
                paginatedItems.map((item, idx) => (
                  <tr key={idx}>
                    <td className="file-cell">
                      <div className="file-info">
                        <FileText size={16} className="text-secondary" />
                        <span>{item.fileName}</span>
                      </div>
                    </td>
                    <td className="time-cell">
                      <div className="time-info">
                        <Clock size={14} />
                        <span>
                          {item.timestamp ? 
                            new Date(item.timestamp).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            }) : 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="version-cell">
                      <span className="version-tag">v{item.version}</span>
                    </td>
                    <td className="comment-cell">
                      <p title={item.comment}>{item.comment || '--'}</p>
                    </td>
                    <td className="actions-cell text-right">
                      {(user.role === 'Admin' || user.role === 'superadmin') && (
                        <button className="firmware-delete-btn" onClick={() => handleDeleteInitiate(item)}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan="5">
                    <div className="empty-content">
                      <History size={40} />
                      <p>No firmware versions found for this category</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

          {/* <div className="pagination">
          <span className="pagination-info">
            Showing {filtered.length > 0 ? indexOfFirst + 1 : 0} to {Math.min(indexOfLast, filtered.length)} of {filtered.length}
          </span>

          <div className="page-controls">
            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
            {getPageNumbers(currentPage, totalPages).map((p, i) => (
              p === '...'
                ? <span key={i} className="page-dots">…</span>
                : <button key={p} className={currentPage === p ? 'active' : ''} onClick={() => handlePageChange(p)}>{p}</button>
            ))}
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>›</button>
            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>»</button>
          </div>
        </div>
      </div> */}

        {/* Pagination */}
        {filteredItems.length > 0 && (
          <div className="pagination">
            <div className="page-info pagination-info">
              Showing {indexOfFirst + 1} to {Math.min(indexOfFirst + rowsPerPage, filteredItems.length)} of {filteredItems.length}
            </div>
            <div className="pagination-controls"> 
              <button className="page-nav page-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
              <button className="page-nav page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>›</button>
              {getPageNumbers().map((p, i) => (
                p === '...'
                  ? <span key={i} className="page-dots">…</span>
                  : <button key={p} className={`page-nav page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => handlePageChange(p)}>{p}</button>
              ))}
              <button className="page-nav page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
              <button className="page-nav page-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add {activeCategory.label} Firmware</h2>
              <button type="button" onClick={closeAddModal}><X size={20} /></button>
            </div>
            <form onSubmit={handleUpload}>
              <div className="modal-body">
                {uploadValidationError && (
                  <div className="firmware-form-alert" role="alert">
                    <AlertCircle size={17} aria-hidden="true" />
                    <span>{uploadValidationError}</span>
                  </div>
                )}
                <div className="form-group">
                  <label>Select Firmware File (.bin)</label>
                  <div className="file-upload-zone" onClick={() => document.getElementById('fw-file').click()}>
                    <UploadCloud size={32} />
                    <span>{file ? file.name : 'Choose a file or drag & drop'}</span>
                    <input
                      id="fw-file"
                      type="file"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        setFile(e.target.files[0]);
                        setUploadValidationError('');
                      }}
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Version Number</label>
                    <input
                      type="text"
                      placeholder="e.g. 1.2.0"
                      value={version}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setVersion(value);
                        setUploadValidationError('');
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Comment</label>
                    <input
                      type="text"
                      placeholder="Release notes..."
                      value={comment}
                      onChange={(e) => {
                        setComment(e.target.value);
                        setUploadValidationError('');
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={ closeAddModal}>Cancel</button>
                <button type="submit" className="btn-primary">Upload Firmware</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal (Secure) */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content secure">
            <div className="modal-header">
              <div className="header-with-shield">
                <ShieldCheck size={24} className="shield-icon" />
                <h2>Secure Deletion</h2>
              </div>
              <button onClick={() => setIsDeleteModalOpen(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSecureDelete}>
              <div className="modal-body">
                <div className="warning-banner">
                  <AlertCircle size={20} />
                  <span>Deleting version <strong>{deleteItem?.version}</strong> for {activeCategory.label}. This action is permanent.</span>
                </div>
                <p className="secure-instruction">Please re-verify your identity to confirm deletion:</p>
                <div className="form-group">
                  <label>Admin Email</label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Admin Password</label>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      required
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-danger">Confirm & Delete</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Uploading Overlay */}
      {isUploading && (
        <div className="upload-overlay">
          <div className="progress-card">
            <UploadCloud size={48} className="upload-anim" />
            <h3>Uploading Firmware...</h3>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <span className="progress-percent">{uploadProgress}%</span>
            <p className="progress-hint">Please don't close the browser until the process is complete.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Firmwares;
